import { Document, Packer, Paragraph, HeadingLevel, Table, TableRow, TableCell, WidthType } from 'docx';
import { processElement } from '../utils';

// Type for document section children (only Paragraph and Table can be direct children)
type DocumentChild = Paragraph | Table;

export const useWordExport = () => {
  // Sanitize text to remove invalid characters that can corrupt Word documents
  const sanitizeText = (text: string): string => {
    if (!text) return ' ';
    // Remove null bytes and other control characters except newlines, carriage returns, and tabs
    let sanitized = '';
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const code = char.charCodeAt(0);
      // Keep printable characters, newline (10), carriage return (13), and tab (9)
      // Remove null (0) and other control characters (1-8, 11-12, 14-31, 127)
      if (
        code === 0 ||
        (code >= 1 && code <= 8) ||
        code === 11 ||
        code === 12 ||
        (code >= 14 && code <= 31) ||
        code === 127
      ) {
        continue; // Skip control characters
      }
      sanitized += char;
    }
    const trimmed = sanitized.trim();
    return trimmed || ' '; // Ensure at least a space
  };

  const flushParagraphBuffer = (paragraphBuffer: string[], children: DocumentChild[]) => {
    if (paragraphBuffer.length > 0) {
      // For desktop Word compatibility, use simpler approach: join lines with newlines
      // and create paragraphs with text property instead of complex TextRun structures
      const sanitizedLines = paragraphBuffer.map((line) => sanitizeText(line)).filter((line) => line.trim().length > 0);

      if (sanitizedLines.length > 0) {
        // Join lines with newline characters for simpler paragraph structure
        // Desktop Word handles this better than complex TextRun arrays
        const combinedText = sanitizedLines.join('\n');

        // Split very long text into multiple paragraphs to avoid issues
        const maxLength = 8000;
        if (combinedText.length > maxLength) {
          for (let i = 0; i < combinedText.length; i += maxLength) {
            const chunk = sanitizeText(combinedText.substring(i, i + maxLength));
            if (chunk.trim().length > 0) {
              children.push(new Paragraph({ text: chunk.trim() }));
            }
          }
        } else {
          children.push(new Paragraph({ text: combinedText.trim() }));
        }
      }
      // Clear the buffer by modifying the array in place
      paragraphBuffer.length = 0;
    }
  };

  const convertMarkdownToWord = async (markdown: string) => {
    try {
      const children: DocumentChild[] = [];

      // Check if content contains HTML elements (from WYSIWYG editor)
      const hasHtmlElements =
        markdown.includes('<table') ||
        markdown.includes('<p') ||
        markdown.includes('<h1') ||
        markdown.includes('<h2') ||
        markdown.includes('<h3') ||
        markdown.includes('<ul') ||
        markdown.includes('<ol') ||
        markdown.includes('<div') ||
        markdown.includes('<strong') ||
        markdown.includes('<em') ||
        markdown.includes('<code') ||
        markdown.includes('<pre') ||
        markdown.includes('<li') ||
        markdown.includes('<hr') ||
        markdown.includes('<br');

      if (hasHtmlElements) {
        // Parse HTML directly - DOMParser automatically decodes HTML entities
        const parser = new DOMParser();
        const doc = parser.parseFromString(markdown, 'text/html');

        // Process the document body - use simpler approach for desktop Word compatibility
        doc.body.childNodes.forEach((child) => {
          if (child.nodeType === Node.ELEMENT_NODE) {
            const element = child as Element;
            const tagName = element.tagName.toLowerCase();

            try {
              // Handle headings with simple text approach
              if (tagName === 'h1' || tagName === 'h2' || tagName === 'h3') {
                const text = sanitizeText(element.textContent || '');
                if (text.trim().length > 0) {
                  const headingLevel =
                    tagName === 'h1'
                      ? HeadingLevel.HEADING_1
                      : tagName === 'h2'
                      ? HeadingLevel.HEADING_2
                      : HeadingLevel.HEADING_3;
                  children.push(new Paragraph({ text: text.trim(), heading: headingLevel }));
                }
              } else if (tagName === 'table') {
                // Process tables using the utility function
                const tableElements = processElement(element);
                tableElements.forEach((el) => {
                  if (el instanceof Table) {
                    children.push(el);
                  }
                });
              } else if (tagName === 'hr') {
                // Horizontal rule - create empty paragraph with spacing
                children.push(
                  new Paragraph({
                    text: ' ',
                    spacing: { after: 200, before: 200 },
                    border: {
                      bottom: {
                        color: '000000',
                        space: 1,
                        style: 'single',
                        size: 6
                      }
                    }
                  })
                );
              } else {
                // For all other elements, extract text content and create simple paragraphs
                const text = sanitizeText(element.textContent || '');
                if (text.trim().length > 0) {
                  // Split long text into multiple paragraphs
                  const maxLength = 8000;
                  if (text.length > maxLength) {
                    for (let i = 0; i < text.length; i += maxLength) {
                      const chunk = sanitizeText(text.substring(i, i + maxLength));
                      if (chunk.trim().length > 0) {
                        children.push(new Paragraph({ text: chunk.trim() }));
                      }
                    }
                  } else {
                    children.push(new Paragraph({ text: text.trim() }));
                  }
                }
              }
            } catch (error) {
              console.warn('Error processing element:', error, child);
              // Fallback: extract text content
              const textContent = element.textContent;
              if (textContent) {
                const sanitized = sanitizeText(textContent);
                if (sanitized.trim().length > 0) {
                  children.push(new Paragraph({ text: sanitized.trim() }));
                }
              }
            }
          } else if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
            // Handle standalone text nodes
            const text = sanitizeText(child.textContent);
            if (text.trim().length > 0) {
              children.push(new Paragraph({ text: text.trim() }));
            }
          }
        });

        // If no children were generated from HTML, try processing as markdown
        if (children.length === 0) {
          // Remove HTML tags and process as plain text
          const plainText = markdown.replace(/<[^>]*>/g, '');
          const sanitized = sanitizeText(plainText);
          if (sanitized.trim().length > 0) {
            // Split very long text into multiple paragraphs
            const maxLength = 8000;
            if (sanitized.length > maxLength) {
              for (let i = 0; i < sanitized.length; i += maxLength) {
                const chunk = sanitizeText(sanitized.substring(i, i + maxLength));
                if (chunk.trim().length > 0) {
                  children.push(new Paragraph({ text: chunk.trim() }));
                }
              }
            } else {
              children.push(new Paragraph({ text: sanitized.trim() }));
            }
          }
        }
      } else {
        // Handle markdown tables
        const lines = markdown.split('\n');
        let inTable = false;
        let tableRows: { text: string }[][] = [];
        const paragraphBuffer: string[] = [];

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const trimmed = line.trim();

          // Check for table rows
          if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            flushParagraphBuffer(paragraphBuffer, children);
            if (!inTable) {
              inTable = true;
              tableRows = [];
            }
            // Parse table row
            const cells = trimmed
              .split('|')
              .slice(1, -1)
              .map((cell) => cell.trim());
            const row = cells.map((cell) => ({ text: cell }));
            tableRows.push(row);
          } else if (trimmed.match(/^\|[\s\-:]+\|$/)) {
            // Skip separator line
          } else {
            // End table if we were in one
            if (inTable && tableRows.length > 0) {
              flushParagraphBuffer(paragraphBuffer, children);
              // Ensure all rows have the same number of cells
              const maxCells = Math.max(...tableRows.map((row) => row.length), 0);
              if (maxCells > 0) {
                const normalizedRows = tableRows.map((row) => {
                  const normalizedRow = [...row];
                  // Pad rows with empty cells if needed
                  while (normalizedRow.length < maxCells) {
                    normalizedRow.push({ text: ' ' });
                  }
                  return normalizedRow;
                });
                const table = new Table({
                  rows: normalizedRows.map(
                    (row) =>
                      new TableRow({
                        children: row.map((cell) => {
                          // Limit cell text length to prevent corruption
                          const cellText = cell.text || ' ';
                          const sanitized = sanitizeText(cellText);
                          const safeText =
                            sanitized.length > 8000 ? sanitizeText(sanitized.substring(0, 8000)) : sanitized;
                          return new TableCell({
                            children: [new Paragraph({ text: safeText.trim() || ' ' })]
                          });
                        })
                      })
                  ),
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE
                  }
                });
                children.push(table);
              }
              inTable = false;
              tableRows = [];
            }
            // Process regular content
            if (trimmed.startsWith('# ')) {
              flushParagraphBuffer(paragraphBuffer, children);
              const headingText = sanitizeText(trimmed.substring(2));
              if (headingText.trim().length > 0) {
                // Limit heading text length
                const safeText = headingText.length > 8000 ? sanitizeText(headingText.substring(0, 8000)) : headingText;
                children.push(new Paragraph({ text: safeText.trim(), heading: HeadingLevel.HEADING_1 }));
              }
            } else if (trimmed.startsWith('## ')) {
              flushParagraphBuffer(paragraphBuffer, children);
              const headingText = sanitizeText(trimmed.substring(3));
              if (headingText.trim().length > 0) {
                const safeText = headingText.length > 8000 ? sanitizeText(headingText.substring(0, 8000)) : headingText;
                children.push(new Paragraph({ text: safeText.trim(), heading: HeadingLevel.HEADING_2 }));
              }
            } else if (trimmed.startsWith('### ')) {
              flushParagraphBuffer(paragraphBuffer, children);
              const headingText = sanitizeText(trimmed.substring(4));
              if (headingText.trim().length > 0) {
                const safeText = headingText.length > 8000 ? sanitizeText(headingText.substring(0, 8000)) : headingText;
                children.push(new Paragraph({ text: safeText.trim(), heading: HeadingLevel.HEADING_3 }));
              }
            } else if (trimmed.startsWith('- ')) {
              flushParagraphBuffer(paragraphBuffer, children);
              const bulletText = sanitizeText(trimmed.substring(2));
              if (bulletText.trim().length > 0) {
                const safeText = bulletText.length > 8000 ? sanitizeText(bulletText.substring(0, 8000)) : bulletText;
                children.push(new Paragraph({ text: safeText.trim(), bullet: { level: 0 } }));
              }
            } else if (trimmed.startsWith('```')) {
              // Skip code block markers
              flushParagraphBuffer(paragraphBuffer, children);
            } else if (trimmed.match(/^[-*_]{3,}$/)) {
              flushParagraphBuffer(paragraphBuffer, children);
              // Handle markdown horizontal rules (three or more dashes, asterisks, or underscores)
              children.push(
                new Paragraph({
                  text: ' ',
                  spacing: {
                    after: 200,
                    before: 200
                  },
                  border: {
                    bottom: {
                      color: '000000',
                      space: 1,
                      style: 'single',
                      size: 6
                    }
                  }
                })
              );
            } else if (trimmed === '') {
              flushParagraphBuffer(paragraphBuffer, children);
            } else {
              paragraphBuffer.push(line);
            }
          }
        }
        // Flush any remaining paragraph buffer
        flushParagraphBuffer(paragraphBuffer, children);
        // Handle table at end of content
        if (inTable && tableRows.length > 0) {
          // Ensure all rows have the same number of cells
          const maxCells = Math.max(...tableRows.map((row) => row.length), 0);
          if (maxCells > 0) {
            const normalizedRows = tableRows.map((row) => {
              const normalizedRow = [...row];
              // Pad rows with empty cells if needed
              while (normalizedRow.length < maxCells) {
                normalizedRow.push({ text: ' ' });
              }
              return normalizedRow;
            });
            const table = new Table({
              rows: normalizedRows.map(
                (row) =>
                  new TableRow({
                    children: row.map((cell) => {
                      // Limit cell text length to prevent corruption
                      const cellText = cell.text || ' ';
                      const sanitized = sanitizeText(cellText);
                      const safeText = sanitized.length > 8000 ? sanitizeText(sanitized.substring(0, 8000)) : sanitized;
                      return new TableCell({
                        children: [new Paragraph({ text: safeText.trim() || ' ' })]
                      });
                    })
                  })
              ),
              width: {
                size: 100,
                type: WidthType.PERCENTAGE
              }
            });
            children.push(table);
          }
        }
      }

      // Ensure we have at least some content
      if (children.length === 0) {
        children.push(new Paragraph({ text: 'No content to export' }));
      }

      // Validate that all children are valid DocumentChild types
      // Don't validate content structure - the docx library handles that
      // Our validation was too strict and filtering out valid paragraphs
      const validChildren = children.filter((child) => {
        const isValid = child instanceof Paragraph || child instanceof Table;
        if (!isValid) {
          console.warn('Skipping invalid child type:', child);
        }
        return isValid;
      });

      if (validChildren.length === 0) {
        // Create a minimal valid document with a single paragraph
        validChildren.push(new Paragraph({ text: 'No content to export' }));
      }

      console.log(`Creating document with ${validChildren.length} children`);

      // Create document with proper structure and required metadata for desktop Word compatibility
      let doc: Document;
      try {
        // Split large documents into multiple sections for better desktop Word compatibility
        // Desktop Word can have issues with very large single sections
        const maxChildrenPerSection = 1000;
        const sections: any[] = [];

        if (validChildren.length <= maxChildrenPerSection) {
          // Single section for smaller documents
          sections.push({
            properties: {
              page: {
                margin: {
                  top: 1440, // 1 inch in twips (20 * 72)
                  right: 1440,
                  bottom: 1440,
                  left: 1440
                },
                size: {
                  width: 12240, // 8.5 inches in twips
                  height: 15840 // 11 inches in twips
                }
              }
            },
            children: validChildren
          });
        } else {
          // Split into multiple sections for large documents
          for (let i = 0; i < validChildren.length; i += maxChildrenPerSection) {
            const sectionChildren = validChildren.slice(i, i + maxChildrenPerSection);
            sections.push({
              properties: {
                page: {
                  margin: {
                    top: 1440,
                    right: 1440,
                    bottom: 1440,
                    left: 1440
                  },
                  size: {
                    width: 12240,
                    height: 15840
                  }
                }
              },
              children: sectionChildren
            });
          }
        }

        doc = new Document({
          creator: 'NPI Canvas Editor',
          title: 'Canvas Document',
          description: 'Exported from Canvas Editor',
          revision: 1,
          compatibility: {
            version: 15 // Word 2013 compatibility mode for better desktop Word support
          },
          defaultTabStop: 720, // 0.5 inch default tab stop
          sections: sections
        });
      } catch (docError) {
        console.error('Error creating Document object:', docError);
        const errorMsg = docError instanceof Error ? docError.message : 'Unknown error';
        throw new Error(`Failed to create document structure: ${errorMsg}`);
      }

      // Use toBase64String for better desktop Word compatibility
      // This method is more reliable for complex documents
      let blob: Blob;
      try {
        // Convert to base64 first, then to blob - this ensures proper encoding
        const base64 = await Packer.toBase64String(doc);

        // Convert base64 to binary string
        const binaryString = atob(base64);

        // Create Uint8Array from binary string
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Create blob with explicit MIME type for desktop Word
        blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });

        // Validate it's a valid ZIP (DOCX is a ZIP archive)
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const isZip =
          uint8Array[0] === 0x50 && uint8Array[1] === 0x4b && uint8Array[2] === 0x03 && uint8Array[3] === 0x04;

        if (!isZip) {
          console.error('Generated blob is not a valid ZIP file');
          throw new Error('Failed to create valid DOCX file structure');
        }
      } catch (packError) {
        console.error('Error packing document to blob:', packError);
        throw new Error(`Failed to pack document: ${packError instanceof Error ? packError.message : 'Unknown error'}`);
      }

      // Validate blob before saving
      if (!blob || blob.size === 0) {
        throw new Error(`Failed to create document blob (size: ${blob?.size || 0})`);
      }

      console.log('Blob created successfully', { size: blob.size, type: blob.type });

      // Create a File object for better compatibility with desktop Word
      const file = new File([blob], 'canvas-document.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        lastModified: Date.now()
      });

      // Use a more reliable save method with explicit file extension
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'canvas-document.docx';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Clean up after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error creating Word document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error creating Word document: ${errorMessage}. Please try again.`);
      throw error; // Re-throw to allow caller to handle if needed
    }
  };

  return { convertMarkdownToWord };
};
